/**
 * How a document's goods travel (piece 22; T115, decision 0682): a small
 * seeded list the Quote, the Sales Order, the Invoice, the Purchase Order and
 * the Work Order name, edited in place on Settings › Documents › Shipment
 * preferences (decision 0604).
 */

/** The four rows the migration seeds, in the order the list draws them. */
export const SHIPMENT_PREFERENCE_SEEDS = ['Our truck', 'Customer pickup', 'Carrier', 'Vendor delivers'] as const;

/** One row as the list and every form's picker read it. */
export interface ShipmentPreferenceRow {
  id: string;
  version: number;
  label: string;
  sortOrder: number;
  isActive: boolean;
  /** Who changed the row last and when, off its audit rows; null on a row nobody has touched (decision 0604). */
  lastChange: { at: string; byName: string } | null;
}

/** The label a row may carry: trimmed, not blank, and at most sixty characters. */
export function shipmentPreferenceLabelProblem(label: string | null | undefined): string | null {
  const trimmed = (label ?? '').trim();
  if (!trimmed) return 'a shipment preference needs a label';
  if (trimmed.length > 60) return 'a shipment preference’s label is at most 60 characters';
  return null;
}

/**
 * 🔒 The choices a form offers: the active rows in their order, plus the row
 * the document already names even where it has since been switched off, so a
 * stored document never reopens with its choice silently cleared.
 */
export function shipmentPreferenceChoices(rows: readonly Pick<ShipmentPreferenceRow, 'id' | 'label' | 'sortOrder' | 'isActive'>[], chosenId: string | null): { value: string; label: string }[] {
  return [...rows]
    .filter((row) => row.isActive || row.id === chosenId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label))
    .map((row) => ({ value: row.id, label: row.label }));
}
